/**
 * Result types for {@link PermissionStore} load and mutation outcomes.
 *
 * Centralised so the store's catchError handlers emit a typed Result
 * instead of swallowing failures via `EMPTY`. Per docs/gaps-fe.md
 * GAP-FE-026: returning a discriminated union forces every internal
 * subscriber (the `tap` operator after `switchMap`) to handle the
 * failure branch explicitly. A guard, banner, or future external
 * consumer can switch on `result.ok` instead of relying on side-channel
 * `*LoadError` signals — eliminating the silent-privilege-escalation
 * surface that prompted this refactor.
 *
 * Pure module — no Angular DI; importable from anywhere.
 */

/**
 * Discriminated union representing the outcome of a permission API call.
 *
 * - `ok: true` — call succeeded, `value` holds the parsed payload.
 * - `ok: false` — call failed, `error` holds the classified failure.
 *
 * Usage inside the store pipeline:
 * ```ts
 * apiCall.pipe(
 *   map((value): PermissionResult<T> => ({ ok: true, value })),
 *   catchError((err): Observable<PermissionResult<T>> =>
 *     of({ ok: false, error: classifyPermissionError(err) })
 *   ),
 * )
 * ```
 */
export type PermissionResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: PermissionLoadError };

/**
 * Classifies why a permission load or mutation failed. The `kind`
 * discriminator lets future consumers (banner UX, auth-state machines,
 * retry logic) switch on the failure category rather than parsing
 * exception messages, and lets compliance reviewers see at a glance
 * which failure modes are auth-related vs. infrastructure-related.
 */
export type PermissionLoadError =
  /** HTTP error with non-success status (4xx/5xx but not 401/403). */
  | { readonly kind: 'http'; readonly status: number; readonly message: string; readonly cause: unknown }
  /** Authentication or authorization failure (401 / 403). */
  | { readonly kind: 'auth'; readonly status: number; readonly message: string; readonly cause: unknown }
  /** Network failure (status 0 — DNS, offline, CORS reject pre-response). */
  | { readonly kind: 'network'; readonly message: string; readonly cause: unknown }
  /** Anything we couldn't parse as a known shape. */
  | { readonly kind: 'unknown'; readonly message: string; readonly cause: unknown };

/**
 * Maps an unknown `catchError` value to a {@link PermissionLoadError}.
 * Recognises `HttpErrorResponse` (Angular's HTTP client wraps failures
 * in this shape) by duck-typing on `status: number`.
 */
export function classifyPermissionError(error: unknown): PermissionLoadError {
  if (error && typeof error === 'object' && 'status' in error) {
    const errAsRecord = error as { status?: unknown; message?: unknown };
    const rawStatus = errAsRecord.status;
    const status = typeof rawStatus === 'number' ? rawStatus : 0;
    const message =
      typeof errAsRecord.message === 'string' && errAsRecord.message.length > 0
        ? errAsRecord.message
        : 'Permission API error';

    if (status === 401 || status === 403) {
      return { kind: 'auth', status, message, cause: error };
    }
    if (status === 0) {
      return { kind: 'network', message, cause: error };
    }
    return { kind: 'http', status, message, cause: error };
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'Unknown permission error';
  return { kind: 'unknown', message, cause: error };
}

/** Type guard: narrows a {@link PermissionResult} to its success branch. */
export function isPermissionResultOk<T>(
  result: PermissionResult<T>
): result is { readonly ok: true; readonly value: T } {
  return result.ok === true;
}
